import { normalize, type CalcInput } from "./calculate";
import { displayAmount, displayPercentDV } from "./rounding";
import { US_NF_NUTRIENTS, displayMicroAmount } from "./nutrients";
import { validate, type ValidationResult } from "./validate";
import type { Market, NutrientId } from "./types";

/** One rendered row of a Nutrition Facts panel. */
export interface PanelRow {
  id: NutrientId;
  /** The row label, e.g. "Total Fat" or "Includes 10g Added Sugars". */
  label: string;
  /**
   * The quantitative amount as a display string. Macros are bare numbers ("8") — the
   * renderer appends the unit; micros already include their unit ("260 mg"). Empty for the
   * Added Sugars row (its amount is baked into `label`).
   */
  amountStr: string;
  /** The %DV display string ("10%"), or null when this row shows no %DV. */
  pdvStr: string | null;
  indent: 0 | 1 | 2;
  bold: boolean;
}

/** The fully-assembled, render-ready Nutrition Facts panel. */
export interface PanelModel {
  kind: "nutrition_facts";
  market: Market;
  title: "Nutrition Facts";
  /** "8 servings per container". */
  servingsLine: string;
  /** "Serving size 2/3 cup (55g)". */
  servingLine: string;
  /** Calories value as a display string (e.g. "230"). */
  calories: string;
  rows: PanelRow[];
  footnotes: string[];
}

const STANDARD_DV_FOOTNOTE =
  "The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes " +
  "to a daily diet. 2,000 calories a day is used for general nutrition advice.";

/** The micros render their amount (incl. unit) via displayMicroAmount, not displayAmount. */
const MICRO: ReadonlySet<NutrientId> = new Set([
  "vitamin_d",
  "calcium",
  "iron",
  "potassium",
]);

/** Mandatory nutrients that, when absent, are reported via a footnote rather than a row. */
const SUPPRESSIBLE: ReadonlySet<NutrientId> = new Set([
  "fat_sat",
  "fat_trans",
  "cholesterol",
  "fiber",
  "sugars_total",
  "sugars_added",
]);

function servingSizeLine(input: CalcInput): string {
  const { householdMeasure, metricGrams } = input.serving;
  return `Serving size ${householdMeasure} (${metricGrams}g)`;
}

function servingsLine(input: CalcInput): string {
  const spc = input.serving.servingsPerContainer;
  if (spc === "varied" || spc == null) return "varied servings per container";
  return `${spc} servings per container`;
}

/**
 * Assemble a complete US Nutrition Facts PanelModel from a CalcInput, plus the validation
 * result. Pure & deterministic: normalize -> per-row displayAmount / displayPercentDV /
 * displayMicroAmount -> ordered rows + serving lines + footnotes.
 *
 * %DV is always computed from the UNROUNDED normalized amount. Whether a row shows its %DV
 * is governed by the nutrient model's `showPercentDV` flag (so Calories, Trans Fat, Total
 * Sugars and Protein carry pdvStr = null even where a DV technically exists).
 */
export function buildPanel(input: CalcInput): {
  panel: PanelModel;
  validation: ValidationResult;
} {
  const validation = validate(input);
  const amounts = normalize(input);
  const { market, profile } = input;

  // Calories (header field, not a row).
  const energy = amounts.get("energy");
  const calories = energy == null ? "0" : displayAmount("energy", market, energy);

  const rows: PanelRow[] = [];
  const footnotes: string[] = [];

  for (const def of US_NF_NUTRIENTS) {
    if (def.id === "energy") continue; // handled as the calories header field

    const amount = amounts.get(def.id);
    if (amount == null) {
      // Absent suppressible nutrient -> "Not a significant source" footnote, no row.
      if (SUPPRESSIBLE.has(def.id)) {
        footnotes.push(`Not a significant source of ${def.names["en-US"]}.`);
      }
      // Absent non-suppressible nutrient is already an error from validate(); skip the row.
      continue;
    }

    const pdvStr = def.showPercentDV
      ? displayPercentDV(def.id, market, profile, amount)
      : null;

    let label: string;
    let amountStr: string;
    if (def.id === "sugars_added" && def.rowTemplate) {
      // The amount is embedded in the label, e.g. "Includes 10g Added Sugars".
      // displayAmount returns either a bare number ("0", "10") that the template's literal
      // "g" turns into a unit, OR the special "less than 1 g" string that ALREADY carries the
      // unit. Strip a trailing " g" from the latter so we render "less than 1g" rather than the
      // doubled "less than 1 gg" (spec golden vector #16: "Includes less than 1g Added Sugars").
      const raw = displayAmount(def.id, market, amount);
      const v = raw.endsWith(" g") ? raw.slice(0, -2) : raw;
      label = def.rowTemplate.replace("{v}", v);
      amountStr = "";
    } else if (MICRO.has(def.id)) {
      label = def.names["en-US"] ?? def.id;
      amountStr = displayMicroAmount(def.id, amount);
    } else {
      label = def.names["en-US"] ?? def.id;
      amountStr = displayAmount(def.id, market, amount);
    }

    rows.push({
      id: def.id,
      label,
      amountStr,
      pdvStr,
      indent: def.indent,
      bold: def.bold,
    });
  }

  footnotes.push(STANDARD_DV_FOOTNOTE);

  const panel: PanelModel = {
    kind: "nutrition_facts",
    market,
    title: "Nutrition Facts",
    servingsLine: servingsLine(input),
    servingLine: servingSizeLine(input),
    calories,
    rows,
    footnotes,
  };

  return { panel, validation };
}
